import React, { useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Icon, Button } from '@picsio/ui';
import * as Icons from '@picsio/icons';
import cn from 'classnames';
import { Interweave } from 'interweave';

import picsioConfig from '../../../../../config';
import localization from '../../shared/strings';
import { navigate } from '../../helpers/history';
import showSendCollectionRecognizeDialog from '../../helpers/showSendCollectionRecognizeDialog';
import Tooltip from '../Tooltip';
import UpgradePlan from '../UpgradePlan';
import Spinner from './Spinner';
import './styles.scss';

const TreePlaceholder = forwardRef(({
  className = '',
  isShownIcon = true,
  isShownCreateButton = true,
  title,
  description = '',
  buttonId = '',
  icon = '',
  createButtonText = '',
  isBusy = false,
  validate = null,
  add = null,
  tooltipAddForm = null,
  isFormEnabled = true,
  isFeatureAllowed = true,
  textForSpinner = '',
  isFaces = false,
  rolePermissions = {},
  onClickUpgradePlan = () => navigate('/billing?tab=overview'),
  footerText = '',
  footerActions = null,
}, ref) => {
  const [isValid, setIsValid] = useState(true);
  const [value, setValue] = useState('');
  const [validationTooltip, setValidationTooltip] = useState(null);

  const applyValidation = useCallback((newValue) => {
    if (!validate) {
      setIsValid(true);
      setValidationTooltip(null);
      return;
    }
    const result = validate(newValue);
    if (result === true) {
      setIsValid(true);
      setValidationTooltip(null);
    } else {
      setIsValid(false);
      setValidationTooltip(typeof result === 'string' ? result : null);
    }
  }, [validate]);

  const addHandler = () => {
    if (value && isValid) {
      if (!isFeatureAllowed) return;
      if (add) add(value);
    } else {
      setIsValid(false);
    }
  };

  /**
   * Handle on input
   * @param {InboxEvent} event
   */
  const handleInputKeyDown = (event) => {
    if (!isFeatureAllowed) return;
    const ENTER = 13;
    const ESC = 27;
    switch (event.keyCode) {
    case ENTER: {
      event.stopPropagation();
      addHandler();
      break;
    }
    case ESC: {
      event.stopPropagation();
      setIsValid(false);
      setValue('');
      setValidationTooltip(null);
      break;
    }
    default: {
      break;
    }
    }
  };

  const handleInputChange = (event) => {
    if (!isFeatureAllowed) return;
    const newValue = event.target.value;
    setValue(newValue);
    applyValidation(newValue);
  };

  useImperativeHandle(ref, () => ({
    addHandler,
  }));

  const handleSendCollectionRecognize = () => {
    showSendCollectionRecognizeDialog({
      title: localization.FACESTREE.senCollectionRecognizeDialogTitle,
      textBtnOk: localization.FACESTREE.textDialogBtnOk,
    });
  };

  const CurrentIcon = Icons[icon] || Icons.Circle;

  return (
    <div className={cn('treePlaceholder', className, { 'treePlaceholder--busy': isBusy })}>
      <If condition={isBusy}>
        <Spinner text={textForSpinner} />
      </If>

      <div className="treePlaceholderTitle">
        {title}{' '}
        {!isFeatureAllowed && <UpgradePlan onClick={onClickUpgradePlan} />}
      </div>

      <If condition={isShownIcon}>
        <div className="treePlaceholderIcon">
          <Icon>
            <CurrentIcon />
          </Icon>
        </div>
      </If>

      <If condition={description}>
        <p className="treePlaceholderDescription">{description}</p>
      </If>

      <If condition={add}>
        <Tooltip
          content={
            validationTooltip
            || (!isFeatureAllowed || !isFormEnabled ? tooltipAddForm : null)
          }
          placement="top"
        >
          <div className="treePlaceholderAddFrom">
            <input
              value={value}
              className={!isValid ? 'error' : null}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              placeholder={picsioConfig.isPim && title === 'Keywords' ? 'Enter a new keyword' : localization.TREE_PLACEHOLDER.enterNewName}
              type="text"
              disabled={!isFeatureAllowed || !isFormEnabled}
              data-testid={`emptyState.${buttonId}Input`}
            />

            <If condition={value && isShownCreateButton}>
              <Button
                className="treePlaceholderAddFrom__button"
                id={`button-${buttonId}`}
                variant="contained"
                color="primary"
                component="button"
                size="md"
                onClick={addHandler}
                disabled={!isValid || !isFormEnabled}
                componentProps={{
                  'data-testid': `emptyState.${buttonId}Button`,
                }}
              >
                {createButtonText || localization.TREE_PLACEHOLDER.createNew}
              </Button>
            </If>
          </div>
        </Tooltip>
      </If>

      <If condition={isFaces}>
        <If
          condition={
            rolePermissions.manageAssetPersons
            || rolePermissions.autogeneratePersons
          }
        >
          <Button
            className={cn(
              'picsioDefBtn btnCallToAction treePlaceholderButton',
              { notUpgrade: !isFeatureAllowed },
            )}
            onClick={handleSendCollectionRecognize}
            type="submit"
          >
            {localization.FACESTREE.textDialogBtnOk}
          </Button>
        </If>
      </If>
      <If condition={footerText}>
        <p className="treePlaceholderFooterText">
          <Interweave content={footerText} />
        </p>
      </If>
      <If condition={footerActions}>
        <div className="treePlaceholderFooterActions">
          {footerActions}
        </div>
      </If>
    </div>
  );
});

TreePlaceholder.propTypes = {
  isShownIcon: PropTypes.bool,
  isShownCreateButton: PropTypes.bool,
  className: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  footerText: PropTypes.string,
  footerActions: PropTypes.node,
  createButtonText: PropTypes.string,
  icon: PropTypes.string,
  buttonId: PropTypes.string,
  error: PropTypes.bool,
  isBusy: PropTypes.bool,
  validate: PropTypes.func,
  add: PropTypes.func,
  isFeatureAllowed: PropTypes.bool,
  textForSpinner: PropTypes.string,
  isFaces: PropTypes.bool,
  rolePermissions: PropTypes.objectOf(PropTypes.bool),
  onClickUpgradePlan: PropTypes.func,
  isFormEnabled: PropTypes.bool,
  tooltipAddForm: PropTypes.string,
};

export default TreePlaceholder;
